import { createSlice, createSelector, createEntityAdapter } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { getActiveDevice, getDevicesCombined } from '../actions/deviceActions';
import { TrailColor, TrailDuration } from './hunter';
import { LatLat } from './user';
import { colors } from '../../utils/colors';
import { uniqueId } from 'lodash';

export const XTREME = 'MiniFinder Xtreme';
export type DeviceIconColorType = {
    suffix: string;
    color: string;
    borderColor: string;
};
export const DeviceIconColors: DeviceIconColorType[] = [
    {
        suffix: '',
        color: colors.white,
        borderColor: colors.black,
    },
    {
        suffix: '-blue',
        color: colors.blue,
        borderColor: colors.blue,
    },
    {
        suffix: '-yellow',
        color: colors.golden,
        borderColor: colors.golden,
    },
    {
        suffix: '-turquoise',
        color: colors.turquoise,
        borderColor: colors.turquoise,
    },
    {
        suffix: '-green',
        color: colors.lightGreen,
        borderColor: colors.lightGreen,
    },
    {
        suffix: '-red',
        color: colors.lightRed,
        borderColor: colors.lightRed,
    },
];

export const deviceAdapter = createEntityAdapter();
export type DevicePosition = {
    id: number;
    latitude: number;
    longitude: number;
    fixtime: string;
    fixTime: string;
    speed: number;
    status: {
        gsmSignalStrength: number;
    };
    attributes: {
        charge?: boolean;
        batteryLevel: number;
        status: string;
    };
};

export type DeviceCommand = {
    id: number;
    command_type: string;
    value: string | number;
    label: string;
};
export type Device = {
    myDevice: boolean;
    hunter: boolean;
    name: string;
    sharingPosition: boolean;
    uniqueId: number;
    sharingAdmin: boolean;
    lastUpdate: string;
    phone: string;
    email: string;
    status: 'online' | 'offline' | 'unknown' | undefined;
    id: number;
    user_id: number;
    isMy: boolean;
    owner: {
        account: any;
    };
    model: string;
    attributes: { color: string };
    data: {
        id: number;
        device_id: number;
        name: string;
        icon: string | undefined;
        photo: string | undefined;
        low_battery_alarm: number;
        geofence_alarm: number;
        bark_detector: number | undefined;
        speed_tracking: number | undefined;
        speed_limit: number | undefined;
        stop_alarm: number;
        power_off: number;
        last_update_interval: number;
        bark_sensitivity: number;
        hunt: number | undefined;
        low_power_mode: number | undefined;
        duration: number | undefined | null;
        update_interval: number | undefined;
        flashlight: number;
        birth: any;
        gender: any;
        weight: any;
        height: any;
        length: any;
        chest: any;
        neck: any;
        breed: any;
        detail: any;
        uniqueid: string;
        comment: string;
    };
    icon: string | undefined;
    photo: string | undefined;
    position: DevicePosition;
    commands: DeviceCommand[];
};

export type BarkDevice = {
    device_id: number;
    stop: number;
    bpm: number;
};

export type Trail = {
    device_id: number;
    positions: LatLat[];
};
export type BarkingTrail = {
    device_id: number;
    positions: [LatLat[]];
};

export type Coordinate = {
    latitude: number;
    longitude: number;
};

const INITIAL_STATE = {
    devices: [] as Device[],
    activeDevice: null as Device | null,
    deviceIdToRerender: null,
    hunterIdToRerender: null,
    trail: [],
    trailColors: {} as TrailColor,
    trailDurations: {} as TrailDuration,
    trails: [] as Trail[],
    barkingDevices: [] as BarkDevice[],
    barkingDevicesBpm: [] as BarkDevice[],
    barkingDevicesToStop: [] as BarkDevice[],
    barkingTrails: [] as BarkingTrail[],
    barkingDevicesStatuses: new Map(),
    shouldRedraw: false,
    shouldRedrawHunterMarker: false,
    barkingTrailsTS: false,
    oldBarking: null as unknown as Coordinate,
    geofencePin: null as unknown as Coordinate,
    lastFetch: false,
    lastSingleLocate: false,
    dogsDropdown: false,
    dogCoords: null as unknown as Coordinate,
};

const deviceReducer = createSlice({
    name: 'device',
    initialState: deviceAdapter.getInitialState(INITIAL_STATE),
    reducers: {
        setActiveDevice(state, action) {
            state.activeDevice = action.payload as Device;
        },
        setDogCoordinates(state, action) {
            state.dogCoords = action.payload;
        },
        setOldBarking(state, action) {
            state.oldBarking = action.payload;
        },
        setGeofencePin(state, action) {
            state.geofencePin = action.payload;
        },
        setBarkingDevicesStatuses(state, action) {
            state.barkingDevicesStatuses = action.payload;
        },
        setDogsDropdown(state, action) {
            state.dogsDropdown = action.payload;
        },
        setDeviceTrailDuration(state, action) {
            state.trailDurations = { ...state.trailDurations, [action.payload.deviceId]: action.payload.duration };
        },
        setDeviceTrailColor(state, action) {
            state.trailColors = { ...state.trailColors, [action.payload.deviceId]: action.payload.color };
        },
        updateDeviceSharing(state, action) {
            state.devices = state.devices?.map((d) => {
                if (d.myDevice && d.id == action.payload.id) {
                    d.sharingPosition = action.payload.value;
                }
                return d;
            });
        },
        updateBarkingDevicesBpm(state, action) {
            const barkingDevicesBpm = state.barkingDevicesBpm;
            let devices = barkingDevicesBpm;
            if (action.payload.add) {
                console.log('');
            } else {
                devices = barkingDevicesBpm?.filter((d) => d.device_id !== action.payload.id);
            }
            state.barkingDevicesBpm = devices;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getDevicesCombined.fulfilled, (state, action) => {
                state.devices = action.payload.devices;
                state.trails = action.payload.deviceTrails;
                state.barkingDevices = action.payload.barkingDevices;
                state.barkingDevicesBpm = action.payload.barks;
                state.barkingDevicesToStop = action.payload.barks;
                state.barkingTrails = action.payload.barkingTrails;
            })
            .addCase(getDevicesCombined.rejected, () => {})
            .addCase(getActiveDevice.fulfilled, (state, action) => {
                state.activeDevice = action.payload;
            })
            .addCase(getActiveDevice.rejected, () => {});
    },
});

export const {
    updateBarkingDevicesBpm,
    setActiveDevice,
    setBarkingDevicesStatuses,
    setDogsDropdown,
    setDeviceTrailDuration,
    setDeviceTrailColor,
    setDogCoordinates,
    setGeofencePin,
    setOldBarking,
    updateDeviceSharing,
} = deviceReducer.actions;

export default deviceReducer.reducer;

const devicesState$ = (s: RootState) => s.device;
const mapState$ = (s: RootState) => s.map;
const userState$ = (s: RootState) => s.user;
export const rawDevices$ = createSelector(devicesState$, (s) => s.devices);
export const rawDogsTrails$ = createSelector(devicesState$, (s) => s.trails);
export const rawBarkingTrails$ = createSelector(devicesState$, (s) => s.barkingTrails);
export const rawDogsTrailsDurations$ = createSelector(devicesState$, (s) => s.trailDurations);
export const dogsTrailColors$ = createSelector(devicesState$, (s) => s.trailColors);
export const disabledDogFilters$ = createSelector(mapState$, (s) => s.disabledDogFilters);
export const user$ = createSelector(userState$, (s) => s.data);

export const groupedDevices$ = createSelector(rawDevices$, (devices) =>
    devices.reduce(
        (grouped, device) => {
            if (device.myDevice) {
                if (!grouped.myDevices) {
                    grouped.myDevices = [];
                }
                grouped.myDevices.push(device);
            } else if (device.myDevice === false && device.hunter === false) {
                if (!grouped.sharedDevices) {
                    grouped.sharedDevices = [];
                }
                grouped.sharedDevices.push(device);
            }

            return grouped;
        },
        {
            myDevices: [] as Device[],
            sharedDevices: [] as Device[],
        },
    ),
);

export const userDevices$ = createSelector(
    rawDevices$,
    (devices) =>
        devices?.filter((d) => d.myDevice === true)?.sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0)),
);
export const currentPosition$ = createSelector(rawDevices$, (devices) => devices?.find((d) => d.isMy === true) ?? null);

export const hunterDevices$ = createSelector(
    rawDevices$,
    (devices) =>
        devices?.filter((d) => d.hunter === true)?.sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0)),
);

export const userAndHunters$ = createSelector(hunterDevices$, user$, (hunters, user) => {
    return [{ id: user?.id, icon: user?.icon, photo: user?.photo, name: user?.name }, ...hunters];
});

export const onlineDevices$ = createSelector(userDevices$, (devices) => devices?.filter((d) => d.status === 'online'));

export const devicesSections$ = createSelector(groupedDevices$, (devices) => {
    const sections = [];

    if (devices.myDevices) {
        sections.push({
            title: 'myDevices',
            data: devices.myDevices,
        });
    }

    if (devices.sharedDevices) {
        sections.push({
            title: 'sharedDevices',
            data: devices.sharedDevices,
        });
    }

    return sections;
});

export const filteredDevicesTrails$ = createSelector(
    rawDogsTrails$,
    rawDogsTrailsDurations$,
    disabledDogFilters$,
    (trails, durations, dogFilters) =>
        trails
            .filter((d) => !dogFilters?.includes(Number(d.device_id)))
            .filter((trail) => {
                return typeof durations === 'object' && durations[`${trail.device_id}`] != 0;
            })
            .map((trail) => {
                const positions = trail.positions.map((pos) => {
                    return {
                        ...pos,
                        lat: pos.latitude,
                        lng: pos.longitude,
                    };
                });

                return { ...trail, positions };
            }),
);
export const filteredBarkingTrails$ = createSelector(
    rawBarkingTrails$,
    rawDogsTrailsDurations$,
    disabledDogFilters$,
    (trails, durations, dogFilters) =>
        trails
            .filter((d) => !dogFilters?.includes(Number(d.device_id)))
            .filter((trail) => {
                return typeof durations === 'object' && durations[`${trail.device_id}`] != 0;
            })
            .map((trail) => {
                const positions = trail.positions.map((el) => {
                    return el.map((pos) => {
                        return {
                            ...pos,
                            lat: pos.latitude,
                            lng: pos.longitude,
                        };
                    });
                });

                return { ...trail, positions };
            }),
);

export const filteredHunterDevices$ = createSelector(rawDevices$, (devices) =>
    devices
        .filter((el) => el.hunter)
        .filter(
            (el) =>
                el.position.latitude &&
                typeof el.position?.latitude === 'number' &&
                typeof el.position?.longitude === 'number',
        ),
);

export const notSharedDevices$ = createSelector(rawDevices$, (devices) =>
    devices.filter((d) => d.myDevice && !d.sharingPosition),
);

export const sharedDevices$ = createSelector(rawDevices$, (devices) =>
    devices.filter((d) => d.myDevice && d.sharingPosition && !d.sharingAdmin),
);

export const filteredDogDevices$ = createSelector(
    rawDevices$,
    disabledDogFilters$,
    (devices, dogFilters) =>
        devices
            ?.filter((el) => !el.hunter)
            .filter((el) => {
                if (!dogFilters) {
                    return true;
                }
                return !dogFilters.includes(el.id);
            })
            .filter(
                (el) =>
                    el.position?.latitude &&
                    typeof el.position?.latitude === 'number' &&
                    typeof el.position?.longitude === 'number' &&
                    el.position?.latitude != 0 &&
                    el.position?.longitude != 0,
            ),
);

export const dogDevices$ = createSelector(rawDevices$, disabledDogFilters$, (devices, dogFilters) =>
    devices
        .filter((el) => !el.hunter)
        .filter((el) => {
            if (!dogFilters) {
                return true;
            }
            return !dogFilters.includes(el.id);
        }),
);
